// ---- Airborne pose + landing dip (gravity plan) -----------------------------
// The procedural flight presentation — reactions.ts's sibling: a vy-driven
// whole-body lean while ballistic (slight lean-back on the rise, a forward tip
// that grows with fall speed) and a brief crouch dip on touchdown (deeper past
// the fall-damage threshold). Operates on bare Object3Ds via WeakMap state —
// the repo's standard transient-pose pattern — so ONE implementation serves the
// local player, remotes, NPCs, and the Skill Sim rig. No authored clips: this
// is deliberately code-driven v1; the studio clip pipeline can replace it.
//
// Ordering rule (the reactions.ts rule): call applyAirborne AFTER the gait/
// updatePlayer pass and the host's position.y write — the lean must win over
// the gait pose, and the dip subtracts from a y the host rewrites every frame.
// Hosts also pass `moving && !air` into the gait so the walk cycle dies mid-air.

import type * as THREE from "three";

const LAND_DUR = 0.22;      // landing-dip length (seconds)
const LAND_DIP = 0.14;      // soft-landing crouch depth (units)
const LAND_DIP_HARD = 0.32; // hard landing (a fall past safeFall)
const EASE = 9;             // lean ease rate (per second) — no pose pops
const RISE_LEAN = -0.10;    // rad at full jump speed (lean back)
const FALL_LEAN = 0.22;     // rad at terminal-ish fall speed (tip forward)

type AirState = { pitch: number; air: boolean; vy: number; land: number; hard: boolean };
const states = new WeakMap<THREE.Object3D, AirState>();

function st(obj: THREE.Object3D): AirState {
  let s = states.get(obj);
  if (!s) states.set(obj, (s = { pitch: 0, air: false, vy: 0, land: 0, hard: false }));
  return s;
}

/** Per-frame: the rig's airborne flag + vertical velocity (drives the lean).
 *  Cheap for grounded rigs that never flew — allocates nothing. */
export function setAirborne(obj: THREE.Object3D, air: boolean, vy: number): void {
  const s = states.get(obj);
  if (!s) {
    if (!air) return;
    st(obj);
  }
  const cur = st(obj);
  cur.air = air;
  cur.vy = vy;
}

/** Start the touchdown crouch dip (`hard` = a fall past the damage threshold). */
export function popLanding(obj: THREE.Object3D, hard = false): void {
  const s = st(obj);
  s.land = LAND_DUR;
  s.hard = hard;
}

/** AFTER the gait pass + the host's position.y write: ease the airborne lean
 *  in/out and play the landing dip. State self-frees once fully at rest, so
 *  rotation.x returns to its other owners (body poses, the NPC lunge). */
export function applyAirborne(obj: THREE.Object3D, dt: number): void {
  const s = states.get(obj);
  if (!s) return;
  const target = s.air
    ? (s.vy > 0 ? RISE_LEAN * Math.min(1, s.vy / 9) : FALL_LEAN * Math.min(1, -s.vy / 15))
    : 0;
  s.pitch += (target - s.pitch) * Math.min(1, dt * EASE);
  if (!s.air && Math.abs(s.pitch) < 1e-3) s.pitch = 0;
  if (s.pitch !== 0) obj.rotation.x = s.pitch;
  if (s.land > 0) {
    s.land = Math.max(0, s.land - dt);
    const k = Math.sin((1 - s.land / LAND_DUR) * Math.PI); // 0 → 1 → 0
    obj.position.y -= (s.hard ? LAND_DIP_HARD : LAND_DIP) * k;
  }
  if (!s.air && s.pitch === 0 && s.land === 0) states.delete(obj);
}
